import { useState } from "react";

import { Button } from "@/components/button";
import { toast } from "@/components/toast/toast-queue";
import { DeleteItemModal } from "@/features/brands/edit/components/shared/DeleteItemModal";
import { deleteAttachment } from "@/lib/api/attachments";

import type { AssetFileRef } from "./types";

type DeleteAllFilesProps = {
  files: AssetFileRef[];
  linkCount: number;
  onDeleted: () => void;
  onRemoveLinks: () => void;
  /** Names the target in the confirmation, e.g. "assets". */
  scope: string;
};

export function DeleteAllFiles({
  files,
  linkCount,
  onDeleted,
  onRemoveLinks,
  scope,
}: DeleteAllFilesProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const itemCount = files.length + linkCount;

  const deleteAll = async () => {
    setIsDeleting(true);

    if (linkCount > 0) onRemoveLinks();

    if (files.length > 0) {
      const results = await Promise.allSettled(
        files.map((file) => deleteAttachment(file.attachmentId)),
      );
      const failedCount = results.filter(
        (result) => result.status === "rejected",
      ).length;

      if (failedCount > 0) {
        toast.add({
          description: "Please try again.",
          title: `Couldn't delete ${failedCount} of ${files.length} files`,
          type: "error",
        });
      }

      onDeleted();
    }

    setIsDeleting(false);
    setIsOpen(false);
  };

  return (
    <>
      <Button
        className="cursor-pointer rounded-md px-sm py-xs text-[13px] font-medium text-text-quaternary outline-hidden transition-colors hover:text-text-primary focus-visible:ring-2 focus-visible:ring-fg-brand-primary"
        onPress={() => setIsOpen(true)}
        variant="unstyled"
      >
        Clear all
      </Button>
      <DeleteItemModal
        bodyText={`Are you sure you want to delete all ${itemCount} ${itemCount === 1 ? "item" : "items"} in ${scope}? This action cannot be undone.`}
        isConfirmLoading={isDeleting}
        isOpen={isOpen}
        itemLabel="items"
        onConfirm={() => void deleteAll()}
        onOpenChange={setIsOpen}
      />
    </>
  );
}
